// Acquisition tracking is confined to public signup pages on the production app.
// Never send auth query parameters (email, secret, returnTo) to Google.
const GA_ID = 'G-4TE1DJ3HCM';
const CONSENT_KEY = 'skygis_google_consent';
const CAMPAIGN_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'gbraid',
  'wbraid',
];
export type GoogleConsent = { analytics: boolean; marketing: boolean };

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let initialized = false;
let loaded = false;
let signupSent = false;
let consent: GoogleConsent | null = null;

export function isSignupTrackingPage() {
  return (
    import.meta.env.PROD &&
    window.location.hostname === 'app.skygis.app' &&
    ['/auth/signup', '/auth/verify'].includes(window.location.pathname)
  );
}

export function readGoogleConsent(): GoogleConsent | null {
  try {
    const saved = JSON.parse(localStorage.getItem(CONSENT_KEY) || 'null');
    return typeof saved?.analytics === 'boolean' && typeof saved?.marketing === 'boolean' ? saved : null;
  } catch {
    return null;
  }
}

export function initSignupTracking() {
  if (initialized || !isSignupTrackingPage()) return;
  initialized = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function () {
      window.dataLayer!.push(arguments);
    };
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  // Set before config, and retain this safe URL across the SPA verification step.
  const location = new URL(window.location.origin + window.location.pathname);
  const params = new URLSearchParams(window.location.search);
  for (const key of CAMPAIGN_KEYS) {
    const value = params.get(key);
    if (value) location.searchParams.set(key, value);
  }
  window.gtag('set', { page_location: location.toString(), page_referrer: safeReferrer() });
  window.gtag('set', 'linker', { domains: ['skygis.cloud', 'skygis.app'], accept_incoming: true });
  const saved = readGoogleConsent();
  if (saved) applyGoogleConsent(saved);
}

function safeReferrer() {
  try {
    const url = new URL(document.referrer);
    return url.origin + url.pathname;
  } catch {
    return '';
  }
}

export function applyGoogleConsent(choice: GoogleConsent) {
  initSignupTracking();
  if (!initialized) return;
  consent = choice;
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(choice));
  } catch {
    /* Session-only consent. */
  }
  window.gtag!('consent', 'update', {
    analytics_storage: choice.analytics ? 'granted' : 'denied',
    ad_storage: choice.marketing ? 'granted' : 'denied',
    ad_user_data: choice.marketing ? 'granted' : 'denied',
    ad_personalization: choice.marketing ? 'granted' : 'denied',
  });
  if ((!choice.analytics && !choice.marketing) || loaded) return;
  loaded = true;
  window.gtag!('js', new Date());
  window.gtag!('config', GA_ID, { send_page_view: false });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);
}

/** Call only after verify-signup succeeds. Bound the wait so blockers cannot hold up signup. */
export async function trackVerifiedSignup(): Promise<void> {
  if (!initialized || !loaded || !consent || (!consent.analytics && !consent.marketing) || signupSent) return;
  signupSent = true;
  await new Promise<void>((resolve) => {
    const timer = window.setTimeout(resolve, 1500);
    const done = () => {
      window.clearTimeout(timer);
      resolve();
    };
    try {
      window.gtag!('event', 'sign_up', {
        send_to: GA_ID,
        method: 'email',
        value: 10,
        currency: 'EUR',
        transport_type: 'beacon',
        event_callback: done,
        event_timeout: 1500,
      });
    } catch {
      done();
    }
  });
}
